import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { WorkspaceFeedItem } from 'api/ExportHubAPI';

interface Props {
  feed: WorkspaceFeedItem[];
  loading: boolean;
  lastSeen: number;
  onMarkSeen: () => void;
}

const KIND_META: Record<string, { icon: React.ReactNode; cls: string }> = {
  product_added: { icon: <AddCircleOutlineIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-created' },
  license_requested: { icon: <ReceiptLongOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-link' },
  license_issued: { icon: <VerifiedOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-created' },
  screening: { icon: <AutoAwesomeIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-screening' },
  screening_issue: { icon: <WarningAmberIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-unlink' },
  activity: { icon: <BoltOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-update' },
  license_overdrawn: { icon: <WarningAmberIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-unlink' },
  license_nearly_exhausted: { icon: <TrendingDownIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-link' },
};

/**
 * Notifications tab (spec §5.4) — the Export-Hub-filtered view of workspace
 * activity (a computed feed; the BITE global bell remains the platform surface).
 * Items deep-link to the relevant product / license; screening issues land on
 * the product detail. "Seen" state is tracked per team in localStorage.
 */
const NotificationsTab: React.FC<Props> = ({
  feed,
  loading,
  lastSeen,
  onMarkSeen,
}) => {
  const history = useHistory();
  // Snapshot the seen cutoff on open so "new" highlights stay stable, then mark
  // everything seen (clears the unread badge).
  const seenRef = useRef(lastSeen);

  // On open: capture the cutoff (seenRef) and mark everything seen once.
  useEffect(() => {
    onMarkSeen();
  }, []);

  const go = (n: WorkspaceFeedItem): void => {
    if (n.product_id && n.database_id) {
      const issue = n.kind === 'screening_issue' ? '?screening=1' : '';
      history.push(
        `/export-hub/databases/${n.database_id}/products/${n.product_id}${issue}`,
      );
    } else if (n.subject_type === 'license' && n.database_id) {
      history.push(`/export-hub/databases/${n.database_id}/licenses`);
    }
  };

  // Group by calendar day.
  const groups: Array<{ day: string; items: WorkspaceFeedItem[] }> = [];
  feed.forEach((n) => {
    const day = moment(n.created_at).format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(n);
    else groups.push({ day: day, items: [n] });
  });

  const dayLabel = (day: string): string => {
    const m = moment(day);
    if (m.isSame(moment(), 'day')) return 'Today';
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return m.format('MMMM D, YYYY');
  };

  return (
    <div className='export-hub-panel'>
      <div className='eh-intg-intro'>
        <h2 className='eh-intg-intro-title'>
          <NotificationsNoneOutlinedIcon
            style={{ fontSize: 20, marginRight: 8, verticalAlign: 'middle' }}
          />
          Notifications
        </h2>
        <p className='eh-intg-intro-sub'>
          Everything happening across your databases — products added, screenings
          run, licenses added and issued, and links changed.
        </p>
      </div>

      {loading && feed.length === 0 ? (
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      ) : feed.length === 0 ? (
        <div className='export-hub-empty'>
          <NotificationsNoneOutlinedIcon
            className='export-hub-empty-icon'
            style={{ fontSize: 32 }}
          />
          <div className='export-hub-empty-title'>You&rsquo;re all caught up</div>
          <div className='export-hub-empty-sub'>
            Activity across your databases will show up here.
          </div>
        </div>
      ) : (
        <div className='eh-notif-feed'>
          {groups.map((g) => (
            <div className='eh-notif-group' key={g.day}>
              <div className='eh-notif-day'>{dayLabel(g.day)}</div>
              {g.items.map((n) => {
                const meta = KIND_META[n.kind] || KIND_META.activity;
                const isNew = new Date(n.created_at).getTime() > seenRef.current;
                const clickable = !!(n.product_id || n.subject_type === 'license');
                return (
                  <button
                    key={n.id}
                    type='button'
                    className={`eh-notif-row ${isNew ? 'unread' : ''} ${
                      clickable ? '' : 'static'
                    }`}
                    onClick={() => go(n)}
                    disabled={!clickable}
                  >
                    <span className={`eh-tl-icon ${meta.cls}`}>{meta.icon}</span>
                    <span className='eh-notif-body'>
                      <span className='eh-notif-text'>
                        {n.title && <strong>{n.title}</strong>}
                        {n.title ? ' — ' : ''}
                        {n.message}
                      </span>
                      <span className='eh-notif-meta'>
                        {n.actor_name || 'Someone'} ·{' '}
                        {moment(n.created_at).fromNow()}
                      </span>
                    </span>
                    {isNew && <span className='eh-notif-newdot' />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
